import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { getProducts } from '../../service/products.service'
import { ProductDto } from '../../service/types'
import { useAppSelector } from '../../store/hooks'
import { isAdminSelector } from '../../store/slices/roleSlice'
import { ProductDialog } from './ProductDialog'

export const Products: FC = () => {
  const { t } = useTranslation()
  const isAdmin = useAppSelector(isAdminSelector)
  const [products, setProducts] = useState<ProductDto[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const filteredProducts = useMemo(
    () => (showInactive ? products : products.filter((p) => p.isActive)),
    [products, showInactive],
  )

  const loadProducts = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getProducts()
      setProducts(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadProducts()
  }, [loadProducts])

  const handleAdd = () => {
    setSelectedProduct(null)
    setDialogOpen(true)
  }

  const handleEdit = (product: ProductDto) => {
    setSelectedProduct(product)
    setDialogOpen(true)
  }

  return (
    <ScreenWrapper
      title={t('products.title')}
      filter={
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} size="small" />}
            label={t('products.showInactive')}
          />
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              {t('products.addProduct')}
            </Button>
          )}
        </Box>
      }
    >
      {loading && <LinearProgress sx={{ mt: 2 }} />}
      <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}></TableCell>
              <TableCell>{t('products.form.name')}</TableCell>
              <TableCell>{t('products.form.lessonType')}</TableCell>
              <TableCell>{t('products.form.durationMinutes')}</TableCell>
              <TableCell>{t('products.form.price')}</TableCell>
              <TableCell>{t('products.form.pricePerAdditionalPerson')}</TableCell>
              <TableCell>{t('products.form.vatRate')}</TableCell>
              <TableCell>{t('products.form.isActive')}</TableCell>
              {isAdmin && <TableCell align="right"></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id} sx={{ opacity: product.isActive ? 1 : 0.5 }}>
                <TableCell sx={{ width: 40, pr: 0 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: product.color,
                    }}
                  />
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>
                  <Chip label={t(`lessonType.${product.lessonType}`)} size="small" />
                </TableCell>
                <TableCell>{product.durationMinutes} min</TableCell>
                <TableCell>{product.price}</TableCell>
                <TableCell>{product.lessonType === 'private' ? product.pricePerAdditionalPerson : '\u2014'}</TableCell>
                <TableCell>{product.vatRate}%</TableCell>
                <TableCell>{product.isActive ? '\u2713' : '\u2014'}</TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(product)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ProductDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void loadProducts()}
        product={selectedProduct}
      />
    </ScreenWrapper>
  )
}
